import { Button, Modal } from '@/components/ui'
import { useT } from '@/internationalization/i18n'

export interface ConfirmDialogProps {
  isOpen: boolean
  onCancel: () => void
  onConfirm: () => void
  title: string
  /** Say what will happen and whether it can be undone. */
  description: string
  confirmLabel?: string
  cancelLabel?: string
  isDestructive?: boolean
  isLoading?: boolean
}

/**
 * Used for anything irreversible: deleting a meeting, revoking a share link,
 * removing a member, disconnecting an integration.
 *
 * Destructive confirms do not close on an overlay click — a misplaced tap must
 * not be able to answer the question.
 */
export function ConfirmDialog({
  isOpen,
  onCancel,
  onConfirm,
  title,
  description,
  confirmLabel,
  cancelLabel,
  isDestructive,
  isLoading,
}: ConfirmDialogProps) {
  const t = useT()
  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      title={title}
      description={description}
      size="sm"
      closeOnOverlayClick={!isDestructive}
      footer={
        <>
          <Button onClick={onCancel} fullWidth className="sm:w-auto">
            {cancelLabel ?? t('common.cancel')}
          </Button>
          <Button
            variant={isDestructive ? 'danger' : 'primary'}
            onClick={onConfirm}
            isLoading={isLoading}
            fullWidth
            className="sm:w-auto"
          >
            {confirmLabel ?? t('common.confirm')}
          </Button>
        </>
      }
    >
      <span className="sr-only">{description}</span>
    </Modal>
  )
}
